import React from 'react';
import axios from 'axios';
import ItemDropdown from '../items/item_dropdown';
import { PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { getHeaders } from '../../lib/requests';

const MenuItems = ({ items, menuItems, handleItemChange, addItem, removeItem, reorderItem, disabled }) => {
  const apiToken = localStorage.getItem('accessToken');
  const apiUrl = process.env.REACT_APP_API_URL;

  const allItemsSelected = menuItems.every(menuItem => menuItem.item_id);

  const handleRemoveItem = async (index, menuItemId) => {
    const confirmed = window.confirm("Are you sure you want to delete this menu item?");
    if (!confirmed) return;
    
    if (menuItemId) {
      try {
        await axios.delete(`${apiUrl}/menu_items/${menuItemId}`, {
          headers: getHeaders(apiToken),
        });
        removeItem(index);
      } catch (error) {
        console.error('Failed to delete menu item', error);
      }
    } else {
      removeItem(index);
    }
  };

  const handleReorder = async (index, newIndex) => {
    if (newIndex < 0 || newIndex >= menuItems.length) return;

    const reorderedItems = [...menuItems];
    const [movedItem] = reorderedItems.splice(index, 1);
    reorderedItems.splice(newIndex, 0, movedItem);

    const updatedItems = reorderedItems.map((item, i) => ({
      ...item,
      sort_order: i + 1,
    }));

    reorderItem(index, newIndex);

    try {
      await Promise.all(
        updatedItems.map((item) =>
          axios.put(`${apiUrl}/menu_items/${item.menu_item_id}`, {
            item_id: item.item_id,
            sort_order: item.sort_order,
          }, {
            headers: getHeaders(apiToken),
          })
        )
      );
    } catch (error) {
      console.error('Failed to update sort order', error);
    }
  };

  return (
    <div className="mb-4">
      {menuItems.length > 0 ? (        
        menuItems.map((menuItem, index) => (
          <div key={index} className="grid grid-cols-10 gap-4 items-center">
            <div className="col-span-8 md:col-span-5 lg:col-span-6">
              <ItemDropdown
                label="Item"
                items={items.filter(
                  (item) =>
                    !menuItems.some(
                      (otherMenuItem, i) =>
                        i !== index && otherMenuItem.item_id === item.item_id
                    )
                )}
                name="item_id"
                value={menuItem.item_id}
                onSelect={(value) => handleItemChange(index, 'item_id', value)}
                disabled={menuItem.loading || disabled}
                showLabels={index === 0}
                showItemLink={true}
              />
            </div>
            <div className="col-span-1 flex items-center">
              <button
                type="button"
                onClick={() => handleReorder(index, index - 1)}
                disabled={index === 0 || disabled}
                className="text-gray-500 hover:text-black ml-2"
              >
                <ChevronUpIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handleReorder(index, index + 1)}
                disabled={index === menuItems.length - 1 || disabled}
                className="text-gray-500 hover:text-black ml-2"
              >
                <ChevronDownIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="col-span-1 flex justify-start items-center">
              <button
                type="button"
                onClick={() => handleRemoveItem(index, menuItem.menu_item_id)}
                className={`text-red-500 hover:text-white hover:bg-red-500 p-2 rounded focus:outline-none transition-colors duration-150 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={disabled}
              >
                <TrashIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center mb-2">
          <p className="text-gray-700">No items added. Click + to add an item.</p>
        </div>
      )}
      {allItemsSelected && !disabled && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={addItem}
            className="mt-2 text-green-500 hover:text-white hover:bg-green-500 p-2 rounded focus:outline-none transition-colors duration-150"
            disabled={disabled}
          >
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuItems;
